import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'
import { ScrollArea } from './ui/scroll-area'
import { ScrollAreaViewport } from '@radix-ui/react-scroll-area'
import { Label } from './ui/label'
import countriesData from '@/core/constants/countries.json'
import translate from '@/core/services/translate'
import { getTranslation } from '@/account/services/auth'
import { cn } from '@/lib/utils'

// Define the country type
export type Country = {
  id: number
  name: string
  flag: string
  phonePrefix: string
  code: string
}

// Example countries data
const countries: Country[] = countriesData

type Props = {
  label?: string
  required?: boolean
  value?: string
  onChange?: (value: string) => void
  withIcon?: boolean
  withCallingCode?: boolean
  disabled?: boolean
  error?: string
  buttonClassName?: string
  labelClassName?: string
}

export const CountrySelect: React.FC<Props> = ({
  label,
  value: valueProp,
  onChange,
  withIcon = true,
  withCallingCode = false,
  disabled = false,
  error,
  required,
  buttonClassName,
  labelClassName,
}) => {
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState(valueProp)
  const [search, setSearch] = React.useState('')
  const language = getTranslation()

  React.useEffect(() => {
    setValue(valueProp)
  }, [valueProp])

  const renderValue = () => {
    if (value) {
      const country = countries.find(
        (country) => country.code.toString() === value,
      )
      return (
        country && (
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2">
              {withIcon && (
                <img
                  src={`data:image/png;base64, ${country.flag}`}
                  alt={country.name}
                  width={24}
                  height={24}
                  className="h-[24px] rounded-full"
                />
              )}
              <span className="text-subtitle-m">{country.name}</span>
            </div>
            {withCallingCode && (
              <span className="ml-auto text-sm">{country.phonePrefix}</span>
            )}
          </div>
        )
      )
    }
    return translate(language.value, 'common.selectCountry')
  }

  return (
    <div className="grid w-full gap-[5px]">
      {label && (
        <Label
          className={cn('text-body-medium text-monochrome-2', labelClassName)}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setSearch('')
        }}
        modal
      >
        <PopoverTrigger asChild>
          <Button
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={twMerge(
              'w-full !shadow-none justify-between px-0 pt-0 pb-3 bg-transparent font-semibold text-base hover:bg-transparent hover:text-monochrome2/40 text-monochrome2/40 border-t-0 rounded-none border-l-0 border-r-0 border-b border-monochrome2/40',
              open && 'border-accent-1',
              !value && 'text-monochrome2 hover:text-monochrome2',
              value && 'text-monochrome1 hover:text-monochrome1',
              error && 'border-b-accent4',
              buttonClassName,
            )}
          >
            {renderValue()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[--radix-popover-trigger-width] p-0 border-monochrome-120 border bg-monochrome360 backdrop-blur-xl rounded-[30px] pb-4">
          <Command
            shouldFilter={false}
            className="bg-transparent text-monochrome2/40"
          >
            <div className="m-5 border rounded-full !border-monochrome-120 bg-monochrome-4-60">
              <CommandInput
                className="placeholder:font-bold placeholder:text-monochrome2 text-monochrome1"
                placeholder="Search Countries..."
                value={search}
                onValueChange={setSearch}
              />
            </div>
            <CommandEmpty className="text-monochrome-120">
              {translate(language.value, 'common.noCountryFound')}
            </CommandEmpty>
            <CommandList>
              <ScrollArea>
                <ScrollAreaViewport className="max-h-60">
                  <CommandGroup className="px-4">
                    {countries
                      .filter((c) => {
                        if (!search) return true
                        const s = search.toLowerCase()
                        return (
                          c.name.toLowerCase().includes(s) ||
                          c.phonePrefix.toLowerCase().includes(s)
                        )
                      })
                      .map((country, index, arr) => (
                        <CommandItem
                          key={country.id}
                          value={country.code}
                          onSelect={(currentValue) => {
                            setValue(currentValue)
                            onChange?.(currentValue)
                            setOpen(false)
                          }}
                          className={twMerge(
                            'bg-transparent border-b font-normal text-[13px] py-[10px] first:pt-0 last:pb-0 text-monochrome1 hover:!text-monochrome1/60 transition-all duration-300 hover:bg-monochrome2/10 data-[selected=true]:text-monochrome1 data-[selected=true]:bg-transparent cursor-pointer rounded-none',
                            'data-[selected=true]:!text-monochrome1/60',
                            index === arr.length - 1 && 'border-b-0',
                          )}
                        >
                          <div className="flex items-center justify-between gap-2 w-full h-[24px]">
                            <div className="flex items-center gap-2 h-[24px]">
                              <img
                                src={`data:image/png;base64, ${country.flag}`}
                                alt={country.name}
                                width={24}
                                height={24}
                                className="rounded-full object-cover h-[24px]"
                              />
                              <span className="text-body-medium">
                                {country.name}
                              </span>
                            </div>
                            <span className="ml-auto text-sm">
                              {country.phonePrefix}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </ScrollAreaViewport>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
